/**
 * The keyboard- and screen-reader-complete equivalent of the map, and the
 * default view on a phone. No information is encoded only by position: order,
 * dependencies and route membership are all stated in words.
 */
import type { JourneyView } from '@dcc/shared';
import { StateChip } from './Primitives';
import { toneFor } from '../lib/tone';
import { relativeTime } from '../lib/format';

export function JourneyList({
  journey,
  selectedId,
  onSelect,
  routeFilter,
}: {
  journey: JourneyView;
  selectedId: string | null;
  onSelect: (nodeId: string) => void;
  routeFilter: string | null;
}) {
  const byId = new Map(journey.nodes.map((node) => [node.id, node]));
  const nodes = journey.nodes.filter((node) => !routeFilter || node.route === routeFilter);

  return (
    <ol className="routelist" aria-label={`Journey steps for ${journey.projectName}`}>
      {nodes.map((node, index) => {
        const tone = toneFor('workItem', node.status);
        const depends = node.dependsOnNodeIds
          .map((id) => byId.get(id)?.title)
          .filter((title): title is string => Boolean(title));
        return (
          <li key={node.id}>
            <button
              type="button"
              className="routelist__item"
              data-tone={tone}
              aria-current={selectedId === node.id ? 'true' : undefined}
              onClick={() => onSelect(node.id)}
            >
              <span className="routelist__index" aria-hidden="true">
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className="row__stack">
                <span className="inline inline--tight">
                  <strong>{node.title}</strong>
                  {node.isCurrentPosition && (
                    <span className="chip" data-tone="active">
                      current position
                    </span>
                  )}
                  {node.onCriticalPath && (
                    <span className="chip" data-tone="neutral">
                      critical path
                    </span>
                  )}
                </span>
                <span className="row__sub">
                  {node.type.replace(/_/g, ' ').toLowerCase()} · route {node.route}
                  {depends.length > 0 && ` · after ${depends.join(', ')}`}
                  {node.agents.length > 0 && ` · ${node.agents.map((a) => a.name).join(', ')}`}
                  {node.ownerName && ` · owner ${node.ownerName}`}
                </span>
                {node.statusCause && <span className="row__sub">{node.statusCause}</span>}
              </span>
              <span className="row__stack" style={{ alignItems: 'flex-end', textAlign: 'right' }}>
                <StateChip kind="workItem" state={node.status} />
                <span className="row__sub">{node.etaAt ? relativeTime(node.etaAt) : '—'}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
